/**
 * The driver UI's few visual rules, as constants. Big, plain, high contrast;
 * assume gloved hands and sun on the screen (v3 section 9).
 */
import type { CSSProperties } from 'react'

export const color = {
  green: '#059669',
  greenDark: '#047857',
  blue: '#2563eb',
  red: '#dc2626',
  redDark: '#b91c1c',
  amber: '#d97706',
  grey: '#6b7280',
  greyLight: '#e5e7eb',
  ink: '#111827',
  paper: '#f9fafb',
  white: '#fff',
}

export const TAP = 56 // minimum tap target height, px - bigger than the 44 px web floor

export const page: CSSProperties = {
  minHeight: '100vh',
  background: color.paper,
  color: color.ink,
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  paddingBottom: 96,
}

export const container: CSSProperties = { padding: 16, maxWidth: 640, margin: '0 auto' }

export const bigButton = (bg: string, disabled = false): CSSProperties => ({
  display: 'block',
  width: '100%',
  minHeight: 72,
  padding: '16px 20px',
  border: 0,
  borderRadius: 14,
  background: disabled ? color.greyLight : bg,
  color: disabled ? color.grey : color.white,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: 0.2,
  cursor: disabled ? 'default' : 'pointer',
  boxShadow: disabled ? 'none' : '0 2px 6px rgba(0,0,0,0.15)',
})

export const button = (bg: string, fg = color.white, disabled = false): CSSProperties => ({
  minHeight: TAP,
  padding: '0 20px',
  border: bg === color.white ? `2px solid ${color.greyLight}` : 0,
  borderRadius: 12,
  background: disabled ? color.greyLight : bg,
  color: disabled ? color.grey : fg,
  fontSize: 18,
  fontWeight: 700,
  cursor: disabled ? 'default' : 'pointer',
})

export const textButton: CSSProperties = {
  minHeight: TAP,
  padding: '0 12px',
  border: 0,
  background: 'transparent',
  color: color.grey,
  fontSize: 16,
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
}

export const card: CSSProperties = {
  background: color.white,
  border: `1px solid ${color.greyLight}`,
  borderRadius: 14,
  padding: 16,
  marginBottom: 12,
}

export const badge = (bg: string): CSSProperties => ({
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: 999,
  background: bg,
  color: color.white,
  fontSize: 14,
  fontWeight: 800,
  letterSpacing: 0.3,
})

export const h1: CSSProperties = { fontSize: 26, fontWeight: 800, margin: '4px 0 8px' }
export const muted: CSSProperties = { color: color.grey, fontSize: 16 }

/** Sticky footer for the one action a screen has. */
export const footer: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
  background: 'rgba(249,250,251,0.96)',
  borderTop: `1px solid ${color.greyLight}`,
  display: 'flex',
  gap: 10,
  maxWidth: 640,
  margin: '0 auto',
}

export const tierColor = (tier: string) => (tier === 'full' ? color.green : color.blue)
export const tierLabel = (tier: string) => (tier === 'full' ? 'Concierge' : 'Doorstep')

export const ITEM_TITLES: Record<string, string> = {
  bag_placed: 'Bag placed in feed storage',
  water: 'Water',
  feeder: 'Feeder',
  grit: 'Grit',
  oyster_shell: 'Oyster shell',
  gate_closed: 'Gate closed behind you',
  wide_shot: 'Wide shot of the setup',
}

/** v3 section 9, exactly. Tap only, never typed. */
export const CONDITIONS: Record<string, { value: string; label: string }[]> = {
  water: [
    { value: 'full', label: 'Full' },
    { value: 'topped_off', label: 'Topped off' },
    { value: 'was_empty', label: 'Was empty' },
    { value: 'na', label: 'N/A' },
  ],
  feeder: [
    { value: 'full', label: 'Full' },
    { value: 'half', label: 'Half' },
    { value: 'low', label: 'Low' },
    { value: 'empty', label: 'Empty' },
  ],
  grit: [
    { value: 'topped', label: 'Topped' },
    { value: 'was_full', label: 'Was full' },
    { value: 'na', label: 'N/A' },
  ],
  oyster_shell: [
    { value: 'topped', label: 'Topped' },
    { value: 'was_full', label: 'Was full' },
    { value: 'na', label: 'N/A' },
  ],
}

export const SKIP_REASONS = ['No one home and no access', 'Gate locked', 'Dog would not let me in', 'Other']

export function fmtDate(iso: string): string {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

export function fmtTime(iso: string | null): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}
